import { validateNumeric2DArray, validateArray, validateShapesMatch, validateFitted } from '../utils/validation.js';
import { mean, variance } from '../utils/stats.js';

export class GaussianNaiveBayes {
  constructor() {
    this.classes = null;
    this.classPriors = null;
    this.means = null;
    this.variances = null;
    this.isFitted = false;
  }

  fit(X, y) {
    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }

    validateNumeric2DArray(X, 'X');
    validateArray(y, 'y');
    validateShapesMatch(X, y);

    this.classes = Array.from(new Set(y)).sort();
    const nFeatures = X[0].length;

    this.classPriors = {};
    this.means = {};
    this.variances = {};

    for (const c of this.classes) {
      const classIndices = y.map((label, i) => label === c ? i : -1).filter(i => i !== -1);
      const classData = classIndices.map(i => X[i]);

      this.classPriors[c] = classData.length / X.length;

      this.means[c] = [];
      this.variances[c] = [];

      for (let j = 0; j < nFeatures; j++) {
        const featureValues = classData.map(row => row[j]);
        this.means[c][j] = mean(featureValues);
        this.variances[c][j] = variance(featureValues, false) + 1e-9;
      }
    }

    this.isFitted = true;
    return this;
  }

  _gaussianProbability(x, mean, variance) {
    const exponent = Math.exp(-Math.pow(x - mean, 2) / (2 * variance));
    return (1 / Math.sqrt(2 * Math.PI * variance)) * exponent;
  }

  predict(X) {
    validateFitted(this.isFitted, 'GaussianNaiveBayes');

    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }

    validateNumeric2DArray(X, 'X');

    return X.map(sample => {
      const posteriors = {};

      for (const c of this.classes) {
        let logPosterior = Math.log(this.classPriors[c]);

        for (let j = 0; j < sample.length; j++) {
          const prob = this._gaussianProbability(sample[j], this.means[c][j], this.variances[c][j]);
          logPosterior += Math.log(prob + 1e-10);
        }

        posteriors[c] = logPosterior;
      }

      return this.classes.reduce((a, b) => posteriors[a] > posteriors[b] ? a : b);
    });
  }

  predictProba(X) {
    validateFitted(this.isFitted, 'GaussianNaiveBayes');

    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }

    validateNumeric2DArray(X, 'X');

    return X.map(sample => {
      const logPosteriors = {};

      for (const c of this.classes) {
        let logPosterior = Math.log(this.classPriors[c]);

        for (let j = 0; j < sample.length; j++) {
          const prob = this._gaussianProbability(sample[j], this.means[c][j], this.variances[c][j]);
          logPosterior += Math.log(prob + 1e-10);
        }

        logPosteriors[c] = logPosterior;
      }

      const logValues = Object.values(logPosteriors);
      const maxLog = Math.max.apply(null, logValues);
      const expSums = {};
      let totalExp = 0;

      for (const c of this.classes) {
        expSums[c] = Math.exp(logPosteriors[c] - maxLog);
        totalExp += expSums[c];
      }

      return this.classes.map(c => expSums[c] / totalExp);
    });
  }

  score(X, y) {
    validateFitted(this.isFitted, 'GaussianNaiveBayes');

    const predictions = this.predict(X);
    const correct = predictions.filter((pred, i) => pred === y[i]).length;
    return correct / y.length;
  }
}

export class MultinomialNaiveBayes {
  constructor(options = {}) {
    this.alpha = options.alpha || 1.0;
    this.classes = null;
    this.classPriors = null;
    this.featureProbs = null;
    this.isFitted = false;
  }

  fit(X, y) {
    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }

    validateNumeric2DArray(X, 'X');
    validateArray(y, 'y');
    validateShapesMatch(X, y);

    this.classes = Array.from(new Set(y)).sort();
    const nFeatures = X[0].length;

    this.classPriors = {};
    this.featureProbs = {};

    for (const c of this.classes) {
      const classIndices = y.map((label, i) => label === c ? i : -1).filter(i => i !== -1);
      const classData = classIndices.map(i => X[i]);

      this.classPriors[c] = classData.length / X.length;

      const featureCounts = Array(nFeatures).fill(0);
      let totalCount = 0;

      for (const sample of classData) {
        for (let j = 0; j < nFeatures; j++) {
          featureCounts[j] += sample[j];
          totalCount += sample[j];
        }
      }

      this.featureProbs[c] = featureCounts.map(count =>
        (count + this.alpha) / (totalCount + this.alpha * nFeatures)
      );
    }

    this.isFitted = true;
    return this;
  }

  predict(X) {
    validateFitted(this.isFitted, 'MultinomialNaiveBayes');

    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }

    validateNumeric2DArray(X, 'X');

    return X.map(sample => {
      const scores = {};

      for (const c of this.classes) {
        let logScore = Math.log(this.classPriors[c]);

        for (let j = 0; j < sample.length; j++) {
          logScore += sample[j] * Math.log(this.featureProbs[c][j] + 1e-10);
        }

        scores[c] = logScore;
      }

      return this.classes.reduce((a, b) => scores[a] > scores[b] ? a : b);
    });
  }

  predictProba(X) {
    validateFitted(this.isFitted, 'MultinomialNaiveBayes');

    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }

    validateNumeric2DArray(X, 'X');

    return X.map(sample => {
      const logScores = {};

      for (const c of this.classes) {
        let logScore = Math.log(this.classPriors[c]);

        for (let j = 0; j < sample.length; j++) {
          logScore += sample[j] * Math.log(this.featureProbs[c][j] + 1e-10);
        }

        logScores[c] = logScore;
      }

      const logValues = Object.values(logScores);
      const maxLog = Math.max.apply(null, logValues);
      const expSums = {};
      let totalExp = 0;

      for (const c of this.classes) {
        expSums[c] = Math.exp(logScores[c] - maxLog);
        totalExp += expSums[c];
      }

      return this.classes.map(c => expSums[c] / totalExp);
    });
  }

  score(X, y) {
    validateFitted(this.isFitted, 'MultinomialNaiveBayes');

    const predictions = this.predict(X);
    const correct = predictions.filter((pred, i) => pred === y[i]).length;
    return correct / y.length;
  }
}
